// ────────────────────────────────────────────────────────────────────
// liveEvents — SSE client for the team-chat realtime stream (roadmap 2.1).
//
// Deliberately NOT the native EventSource: it can't set an Authorization
// header (or X-Tenant-Id), and this API is Bearer-gated same as every other
// endpoint (see server/routers/events.py's module docstring). So this reads
// the stream by hand: fetch() + ReadableStream + TextDecoder, same auth
// convention as api.ts (Bearer token + staff X-Tenant-Id).
//
// Poke-not-payload: every `data:` frame is `{"kind": "..."}` — never a
// message body. The caller's job is to refetch its own authoritative query
// on a poke, not to trust anything IN the poke.
// ────────────────────────────────────────────────────────────────────
import { getActiveTenant, getCachedUser, getToken, STAFF_ROLES } from "./api";

const STREAM_URL = "/api/events/stream";

// 1s -> 2s -> 5s -> 10s, capped — resets to the front of the list on any
// successful connection open (a real disconnect after a long healthy run
// should reconnect fast, not inherit a stale backoff).
const RECONNECT_DELAYS_MS = [1_000, 2_000, 5_000, 10_000];

function authHeaders(): Record<string, string> {
  const headers: Record<string, string> = { Authorization: `Bearer ${getToken() ?? ""}` };
  // Same convention as api.ts's request interceptor: only a STAFF caller
  // viewing a chosen tenant sends X-Tenant-Id; customers are tenant-locked
  // server-side and must never send it.
  const tenant = getActiveTenant();
  const user = getCachedUser();
  if (tenant && user && (STAFF_ROLES as readonly string[]).includes(user.role)) {
    headers["X-Tenant-Id"] = tenant;
  }
  return headers;
}

/** Parses one already-decoded SSE line. Returns the poke's `kind`, or null
 *  for anything that isn't a well-formed `data:` poke frame — a comment
 *  (`: ping`), the `event: hello` line, its `data: {}` companion (no
 *  `kind`), a blank keep-alive line, or a malformed JSON body. Exported
 *  standalone so the reconnect/backoff plumbing around it can be tested
 *  without mocking a whole ReadableStream for every case. */
export function parseSseLine(line: string): string | null {
  if (!line.startsWith("data:")) return null;
  const payload = line.slice(5).trim();
  if (!payload) return null;
  let parsed: unknown;
  try {
    parsed = JSON.parse(payload);
  } catch {
    return null;   // malformed frame — never throw out of the reader loop
  }
  if (!parsed || typeof parsed !== "object") return null;
  const kind = (parsed as Record<string, unknown>)["kind"];
  return typeof kind === "string" ? kind : null;
}

/** Subscribes to the tenant's live event stream, calling `onPoke(kind)` for
 *  every poke frame received. Auto-reconnects with backoff on any drop
 *  (network blip, server restart, proxy idle-timeout) — the caller never
 *  has to think about reconnection. Returns an unsubscribe function that
 *  aborts the in-flight request and stops all future reconnect attempts;
 *  safe to call more than once.
 *
 *  Silent no-op (no fetch ever issued) when there's no auth token yet — the
 *  caller (TeamChatDockMounter) is mounted unconditionally, including
 *  before login, and must never spam a 401 in a reconnect loop. */
export function subscribeLiveEvents(onPoke: (kind: string) => void): () => void {
  if (!getToken()) return () => { /* no-op: never subscribed */ };

  let stopped = false;
  let controller: AbortController | null = null;
  let attempt = 0;
  let retryTimer: ReturnType<typeof setTimeout> | null = null;

  const scheduleReconnect = () => {
    if (stopped) return;
    const delay = RECONNECT_DELAYS_MS[Math.min(attempt, RECONNECT_DELAYS_MS.length - 1)];
    attempt += 1;
    retryTimer = setTimeout(connect, delay);
  };

  const connect = () => {
    if (stopped || !getToken()) return;   // logged out while a reconnect was pending
    controller = new AbortController();
    void (async () => {
      try {
        const res = await fetch(STREAM_URL, { headers: authHeaders(), signal: controller!.signal });
        if (!res.ok || !res.body) throw new Error(`live events stream ${res.status}`);
        attempt = 0;   // reset backoff — this connection opened cleanly
        const reader = res.body.getReader();
        const decoder = new TextDecoder();
        let buf = "";
        while (!stopped) {
          const { value, done } = await reader.read();
          if (done) break;
          buf += decoder.decode(value, { stream: true });
          const lines = buf.split("\n");
          buf = lines.pop() ?? "";   // last (possibly partial) line carries over
          for (const line of lines) {
            const kind = parseSseLine(line);
            if (kind) onPoke(kind);
          }
        }
      } catch {
        // Abort (from unsubscribe), network drop, non-2xx — all fall through
        // to the same reconnect-with-backoff path below. Nothing here is
        // fatal; the fallback poll (see TeamChatDockMounter) covers the gap.
      } finally {
        scheduleReconnect();
      }
    })();
  };

  connect();

  return () => {
    stopped = true;
    if (retryTimer) clearTimeout(retryTimer);
    controller?.abort();
  };
}
